import clsx from "clsx";
import type { DecimalString } from "@/api/types";
import { formatPercentNumber } from "@/lib/format";
import { useCssVars } from "@/lib/cssVars";
import { Decimal } from "@/lib/decimal";
import styles from "./UsageRangeBar.module.css";

export interface UsageRange {
  p25_pct: DecimalString | null;
  median_pct: DecimalString | null;
  p75_pct: DecimalString | null;
  max_pct: DecimalString | null;
  n_formulas: number;
}

/** Log axis from 0.001 % to 100 %: usage spans five orders of magnitude, so a shared axis compares rows. */
const LOG_MIN = -3;
const LOG_MAX = 2;

function toNumber(value: DecimalString | null): number | null {
  return Decimal.tryFrom(value)?.toNumber() ?? null;
}

function logPosition(value: number | null): number | null {
  if (value === null || value <= 0) return null;
  const position = (Math.log10(value) - LOG_MIN) / (LOG_MAX - LOG_MIN);
  return Math.min(1, Math.max(0, position)) * 100;
}

export function percentText(value: DecimalString | null): string {
  return `${formatPercentNumber(value, { minDecimals: 0, maxDecimals: 3 })}\u00a0%`;
}

/** "Typical 0.5–2 %, median 1.2 %, up to 5 % in 23 formulas" (the bar's text alternative). */
export function usageSummary(range: UsageRange): string {
  const formulas = `${range.n_formulas} ${range.n_formulas === 1 ? "formula" : "formulas"}`;
  return `Typical ${percentText(range.p25_pct)}–${percentText(range.p75_pct)}, median ${percentText(range.median_pct)}, up to ${percentText(range.max_pct)} in ${formulas}`;
}

/**
 * The middle half of a material's doses (p25–p75) as a band, the median as a tick and the maximum as a
 * dot, on a log axis shared by every bar. The visual is decorative; the summary text carries the values.
 */
export function UsageRangeBar({
  range,
  showScale = false,
  className,
}: {
  range: UsageRange;
  showScale?: boolean;
  className?: string;
}) {
  const p25 = logPosition(toNumber(range.p25_pct));
  const p75 = logPosition(toNumber(range.p75_pct));
  const median = logPosition(toNumber(range.median_pct));
  const max = logPosition(toNumber(range.max_pct));
  const ref = useCssVars<HTMLDivElement>({
    "--band-start": p25 === null ? null : `${p25}%`,
    "--band-end": p75 === null ? null : `${p75}%`,
    "--median": median === null ? null : `${median}%`,
    "--max": max === null ? null : `${max}%`,
  });
  return (
    <div className={clsx(styles.root, className)}>
      <div ref={ref} className={styles.track} aria-hidden="true">
        {p25 !== null && p75 !== null ? <span className={styles.band} /> : null}
        {median !== null ? <span className={styles.median} /> : null}
        {max !== null ? <span className={styles.max} /> : null}
      </div>
      {showScale ? (
        <div className={styles.scale} aria-hidden="true">
          {["0.001 %", "0.01 %", "0.1 %", "1 %", "10 %", "100 %"].map((label) => (
            <span key={label}>{label}</span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
